import React, { useEffect, useState, useRef } from "react";
import SideBar from "../Sidebar/SideBar";
import Package from "../../Api/Package";
import { Icon } from "@iconify/react/dist/iconify.js";
import JoditEditor from "jodit-react";
import Swal from "sweetalert2";
import PlanMaster from "../../Api/PlanMaster";

// ── Multi-select dropdown component ──────────────────────────────────────────
const MultiSelectDropdown = ({ options, value, onChange, placeholder = "----Select----" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const getOptId = (opt) => String(opt.Id ?? opt.id ?? opt.ID ?? "");

  // value is comma-separated string e.g. "1,3,5"
  const selected = value ? value.split(",").filter(Boolean) : [];

  const toggle = (optId) => {
    const strId = String(optId);
    const isSelected = selected.includes(strId);
    const next = isSelected
      ? selected.filter((v) => v !== strId)
      : [...selected, strId];
    onChange(next.join(","));
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayLabel =
    selected.length === 0
      ? placeholder
      : options
        .filter((o) => selected.includes(getOptId(o)))
        .map((o) => o.PlanName)
        .join(", ");

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      {/* Trigger */}
      <div
        className="modify_im_input mode_category1"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          userSelect: "none",
          overflow: "hidden",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
          {displayLabel}
        </span>
        <Icon
          icon={open ? "lucide:chevron-up" : "lucide:chevron-down"}
          style={{ flexShrink: 0, marginLeft: 4 }}
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 2px)",
            left: 0,
            right: 0,
            zIndex: 99999,
            background: "#ffffff",
            border: "1px solid #ccc",
            borderRadius: 4,
            maxHeight: 220,
            overflowY: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {options.length === 0 ? (
            <div style={{ padding: "8px 12px", color: "#888", fontSize: 13 }}>No options</div>
          ) : (
            options.map((opt) => {
              const optId = getOptId(opt);
              const isChecked = selected.includes(optId);

              return (
                <div
                  key={optId}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(optId);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: 14,
                    color: "#333",
                    backgroundColor: isChecked ? "#f0f4ff" : "#ffffff",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isChecked ? "#e8eeff" : "#f5f5f5";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isChecked ? "#f0f4ff" : "#ffffff";
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(optId)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 15, height: 15, accentColor: "#4f46e5", flexShrink: 0 }}
                  />
                  <span>{opt.PlanName}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const Packages = () => {
  const [itemId, setitemId] = useState("");
  const [inputs, setInputs] = useState({
    name: "",
    price: "",
    TextContent: "",
    priority: "",
    isActive: true,
    categoryId: "",
    minSalePrice: "",
    max_amount: "",
    redirectLink: "",
    noOfDays: "",
    expiryUnit: "",
  });
  const [data, setData] = useState([]);
  const [planData, setPlanData] = useState([]);
  const [isAutoDescription, setIsAutoDescription] = useState(true);
  const [isAutoPrice, setIsAutoPrice] = useState(true);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "price") {
      setIsAutoPrice(false); // user took control
    }

    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ saves into categoryId — matches what we pass to MultiSelectDropdown
  const handlePlanChange = (commaSeparated) => {
    setInputs((prev) => ({ ...prev, categoryId: commaSeparated }));
  };

  const handleEditorChange = (newContent) => {
    setIsAutoDescription(false);
    setInputs((prev) => ({ ...prev, TextContent: newContent }));
  };

  const resetInputs = () => {
    setInputs({
      name: "",
      price: "",
      TextContent: "",
      priority: "",
      isActive: true,
      categoryId: "",
      minSalePrice: "",
      max_amount: "",
      redirectLink: "",
      noOfDays: "",
      expiryUnit: "",
    });
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();

    return `${dd}-${mm}-${yyyy}`;
  };

  const handleSave = async () => {
    const response = await Package.CreatePackage(inputs);
    if (response.IsSuccess) {
      fetchData();
      resetInputs();
    }
  };

  const handleUpdate = async () => {
    const response = await Package.UpdatePackageById(itemId, inputs);
    if (response.IsSuccess) {
      Swal.fire({ title: "Updated", text: "Package Updated Successfully.", icon: "info", width: "320px" });
      setitemId("");
      resetInputs();
      fetchData();
    }
  };

  const handleEdit = (item) => {
    setitemId(item.id);
    setInputs({
      name: item.name,
      price: item.price,
      TextContent: item.TextContent,
      priority: item.priority,
      isActive: item.isActive,
      categoryId: item.categoryId ?? "",
      minSalePrice: item.minSalePrice,
      max_amount: item.max_amount,
      redirectLink: item.redirectLink,
      noOfDays: item.noOfDays,
      expiryUnit: item.expiryUnit,
    });
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete this package?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "No, keep it",
      reverseButtons: true,
      width: "340px",
    });
    if (!result.isConfirmed) return;
    try {
      const response = await Package.DeletePackageById(id);
      fetchData();
      if (response.StatusCode === 200)
        Swal.fire({ title: "Deleted", text: "Package Deleted Successfully.", icon: "success", width: "320px" });
    } catch (error) {
      Swal.fire({ title: "Error", text: error?.message || error, icon: "error", width: "320px" });
    }
  };

  const fetchData = async () => {
    const response = await Package.GetAllPackage();
    if (response) setData(response.Result);
  };

  const getAllPlans = async () => {
    const response = await PlanMaster.GetAllPlans();
    if (response?.IsSuccess) setPlanData(response.Result);
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { getAllPlans(); }, []);

  const buildPlanDescription = (selectedIds) => {
    if (!selectedIds) return "";

    const ids = selectedIds.split(",").filter(Boolean);

    const descriptions = planData
      .filter((p) => ids.includes(String(p.Id ?? p.id ?? p.ID)))
      .map((p) => p.PlanDescription)
      .filter(Boolean);

    return descriptions.join("<br/>"); // or "\n"
  };


  useEffect(() => {
    const desc = buildPlanDescription(inputs.categoryId);
    setInputs((prev) => ({
      ...prev,
      TextContent: desc,
    }));
  }, [inputs.categoryId, planData]);


  useEffect(() => {
    if (!isAutoDescription) return;
    const desc = buildPlanDescription(inputs.categoryId);
    setInputs((prev) => ({
      ...prev,
      TextContent: desc,
    }));
  }, [inputs.categoryId, planData]);

  
  const buildPlanPrice = (selectedIds) => {
    if (!selectedIds) return 0;

    const ids = selectedIds.split(",").filter(Boolean);

    const total = planData
      .filter((p) => ids.includes(String(p.Id ?? p.id ?? p.ID)))
      .reduce((sum, p) => sum + Number(p.Price || 0), 0);

    return total;
  };

  useEffect(() => {
    const desc = buildPlanDescription(inputs.categoryId);
    const price = buildPlanPrice(inputs.categoryId);

    setInputs((prev) => ({
      ...prev,
      TextContent: desc,
      price: price,
    }));
  }, [inputs.categoryId, planData]);

  useEffect(() => {
    const desc = buildPlanDescription(inputs.categoryId);
    const autoPrice = buildPlanPrice(inputs.categoryId);

    setInputs((prev) => ({
      ...prev,
      TextContent: desc,
      price: isAutoPrice ? autoPrice : prev.price,
    }));
  }, [inputs.categoryId, planData]);

  return (
    <div className="Flex-general-client-sidebar">
      <div className="sidebar-gen-width"><SideBar /></div>
      <div className="Main-general-Client">
        <div className="header-general">
          <h2 className="general-card-title">Packages</h2>
        </div>
        <div className="control_radius">
          <div className="company_control">
            <div className="stock-im-dep1">
              <div className="input-box-Imo1 input-height2">
                <div className="input-box-mok1">
                  <input className="modify_im_input mode_category1" type="text" name="name" value={inputs.name} onChange={handleChange} required />
                  <label className="modify_p_text2">Name</label>
                </div>
              </div>
            </div>
            <div className="stock-im-dep1">
              <div className="input-box-Imo1 input-height2">
                <div className="input-box-mok1">
                  <input
                    className="modify_im_input mode_category1"
                    type="text"
                    name="price"
                    value={inputs.price}
                    onChange={handleChange}
                  />
                  <label className="modify_p_text2">Price</label>
                </div>
              </div>
            </div>
          </div>

          <div className="company_control">
            {/* ✅ value={inputs.categoryId} and onChange={handlePlanChange} — both consistent */}
            <div className="stock-im-dep1">
              <div className="input-box-Imo1 input-height2">
                <div className="input-box-mok1">
                  <MultiSelectDropdown
                    options={planData}
                    value={inputs.categoryId}
                    onChange={handlePlanChange}
                    placeholder=""
                  />
                  <label className="modify_lbl_text3">Plans</label>
                </div>
              </div>
            </div>
            <div className="stock-im-dep1">
              <div className="input-box-Imo1 input-height2">
                <div className="input-box-mok1">
                  <input className="modify_im_input mode_category1" type="text" name="minSalePrice" value={inputs.minSalePrice} onChange={handleChange} required />
                  <label className="modify_p_text2">Min Sale Price</label>
                </div>
              </div>
            </div>
          </div>

          <div className="company_control">
            <div className="stock-im-dep1">
              <div className="input-box-Imo1 input-height2">
                <div className="input-box-mok1">
                  <input className="modify_im_input mode_category1" type="text" name="redirectLink" value={inputs.redirectLink} onChange={handleChange} required />
                  <label className="modify_lbl_text3">Redirect Link</label>
                </div>
              </div>
            </div>
            <div className="stock-im-dep1">
              <div className="input-box-Imo1 input-height2">
                <div className="input-box-mok1" style={{ display: "flex" }}>
                  <input className="modify_im_input mode_category1" type="number" name="noOfDays" value={inputs.noOfDays} onChange={handleChange} required />
                  <select className="modify_im_input mode_category1" name="expiryUnit" onChange={handleChange} value={inputs.expiryUnit} required>
                    <option value="">Select Days/Months/Years</option>
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                  <label className="modify_p_text2">Package Expiry days</label>
                </div>
              </div>
            </div>
          </div>

          <div className="company_control">
            <div className="stock-im-dep1">
              <div className="input-box-Imo1 input-height2">
                <div className="input-box-mok1">
                  <input className="modify_im_input mode_category1" type="text" name="max_amount" value={inputs.max_amount} onChange={handleChange} required />
                  <label className="modify_p_text2">Max Sale Price</label>
                </div>
              </div>
            </div>
            <div className="stock-im-dep1">
              <div className="input-box-Imo1 input-height2">
                <div className="input-box-mok1">
                  <input className="modify_im_input mode_category1" type="number" name="priority" value={inputs.priority} onChange={handleChange} required />
                  <label className="modify_p_text2">Priority</label>
                </div>
              </div>
            </div>
            <div className="stock-im-dep1">
              <div className="input-box-Imo1 input-height2">
                <div className="input-box-mok1">
                  <select name="isActive" value={inputs.isActive} onChange={handleChange} className="modify_im_input mode_category1">
                    <option value="">----Select----</option>
                    <option value={true}>Active</option>
                    <option value={false}>InActive</option>
                  </select>
                  <label className="modify_p_text2">Is Active</label>
                </div>
              </div>
            </div>
          </div>

          <div className="table-account-manager">
            <JoditEditor value={inputs.TextContent} onChange={handleEditorChange} />
          </div>

          <div className="ButtonTabSave">
            {itemId ? (
              <button id="SaveBtnMain" className="update-button-main-save hovered" onClick={handleUpdate}>Update</button>
            ) : (
              <button id="SaveBtnMain" className="save-button-main-save hovered" onClick={handleSave}>Save</button>
            )}
          </div>

          <div className="company_info-text-im depart_top">Packages List</div>
          <div className="control_radius">
            <div className="table-account-manager">
              <table className="table-master-acc">
                <thead className="header-table-account">
                  <tr>
                    <th className="table-header-manager ac-sno-wth">S.No</th>
                    <th className="table-header-manager">Name</th>
                    <th className="table-header-manager">Price</th>
                    <th className="table-header-manager">Min Sale Price</th>
                    <th className="table-header-manager">Max Sale Price</th>
                    <th className="table-header-manager">Creation Date</th>
                    <th className="table-header-manager">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, idx) => (
                    <tr key={item.id}>
                      <td className="table-data-account ac-sno-wth">{idx + 1}</td>
                      <td className="table-data-account">{item.name}</td>
                      <td className="table-data-account">{item.price}</td>
                      <td className="table-data-account">{item.minSalePrice}</td>
                      <td className="table-data-account">{item.max_amount}</td>
                      <td className="table-data-account">{formatDate(item.cr_dt)}</td>
                      <td className="table-data-account">
                        <button className="edit-btn-acc" onClick={() => handleEdit(item)}><Icon icon="lucide:edit" /></button>
                        <button className="delete-btn-acc" onClick={() => handleDelete(item.id)}><Icon icon="material-symbols:delete" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Packages;